const SEARCH_URL = "search"

export const useSearchStore = defineStore("search", () => {
    const mainStore = useMainStore()
    const bookStore = useBooksStore()

    const lastInputFound = ref<string>("")
    const results = ref<SearchResultsComplete>({
        KLICOVA_SLOVA: [],
        TEMATA: [],
        METRIKY: [],
        KLOK: [],
        STRANKY: [],
        LIMITY: [],
        JEVY: [],
        INDIKATORY: [],
        ATLAS: [],
        VRSTVY: [],
    })
    const fetching = ref<boolean>(false)

    const getSearchResultsTemata = computed(() => {
        // téma 0000 je skrytý odkaz na stiahnutie knihy Kraj
        return results.value.TEMATA.filter(el => el.ID_METADATA && !el.DOKUMENT)
    })

    const getSearchResultsKeStazeni = computed(() => {
        const keStazeni = results.value.TEMATA.filter(el => el.DOKUMENT).map(
            el => {
                const { t } = useI18n()
                return {
                    ...el,
                    HTML: t("search.kestazeni_disclaimer"),
                }
            }
        )
        return keStazeni
    })

    const executeSearch = async (input: string, lang: LanguageCode = "cs") => {
        mainStore.mobileMenuOpen = false
        fetching.value = true
        const localePath = useLocalePath()
        let searchUrl = SEARCH_URL + "?text=" + input
        if (lang === "en") {
            searchUrl += "&lang=en"
        }
        try {
            const data = await $fetch<SearchResults>(searchUrl, {
                baseURL: import.meta.client
                    ? mainStore.apiBaseUrl
                    : mainStore.apiBaseUrlInt,
            })
            lastInputFound.value = input
            const stranky = [
                ...searchInBooks(input, lang),
                ...searchInPages(input),
            ]
            const vrstvy = searchVrstvy(input)
            results.value = {
                ...data,
                STRANKY: stranky,
                VRSTVY: vrstvy,
            }
            // console.log(results.value)
            await navigateTo(
                localePath({ name: "vyhledavani", query: { text: input } })
            )
        } catch (error) {
            console.log(error)
        } finally {
            fetching.value = false
        }
    }

    const searchInPages = (input: string) => {
        const foundPageResults: PageItem[] = []
        const foundPageResultIds: string[] = []
        const keywords = searchKeywords(input)
        // console.log(keywords)
        mainStore.getAllPages.forEach(page => {
            // search page keywords
            const hasFoundKws = keywords.some(kw => {
                if ("keywords" in page) {
                    return page.keywords?.split(",").includes(kw.ID.toString())
                }
            })
            if (hasFoundKws) {
                foundPageResults.push(page)
                foundPageResultIds.push(page.to)
            }
            // search page name
            if (
                includesWithoutDiacritics(
                    useLocaleValue(page, "title"),
                    input
                ) &&
                !foundPageResultIds.includes(page.to)
            ) {
                foundPageResults.push(page)
                foundPageResultIds.push(page.to)
            }
            // seach page description
            if (
                includesWithoutDiacritics(
                    useLocaleValue(page, "description"),
                    input
                ) &&
                !foundPageResultIds.includes(page.to)
            ) {
                foundPageResults.push(page)
                foundPageResultIds.push(page.to)
            }
        })
        return foundPageResults
    }
    const searchInBooks = (input: string, lang: LanguageCode = "cs") => {
        const foundBookResults: BookItem[] = []
        bookStore.books[lang].forEach(book => {
            if (
                includesWithoutDiacritics(book.title, input) ||
                includesWithoutDiacritics(book.subtitle, input) ||
                book.kod.includes(input)
            ) {
                foundBookResults.push(book)
            }
        })
        return foundBookResults
    }
    const searchKeywords = (input: string) => {
        const foundKws = mainStore.keywords.filter(keyword =>
            useLocaleValue(keyword, "NAZEV").includes(input)
        )
        return foundKws
    }
    const searchVrstvy = (input: string) => {
        const foundVrstvy = mainStore.tooltips.vrstvy.filter(
            keyword =>
                useLocaleValue(keyword, "NAZEV")
                    .toLowerCase()
                    .includes(input.toLowerCase()) ||
                keyword.KOD.toLowerCase().includes(input.toLowerCase())
        )
        return foundVrstvy.map(vrstva => vrstva.KOD)
    }
    const resetSearch = () => {
        results.value = {
            KLICOVA_SLOVA: [],
            TEMATA: [],
            METRIKY: [],
            KLOK: [],
            STRANKY: [],
            LIMITY: [],
            JEVY: [],
            INDIKATORY: [],
            ATLAS: [],
            VRSTVY: [],
        }
        lastInputFound.value = ""
    }
    const includesWithoutDiacritics = (str1: string, str2: string) => {
        const str1WithoutDiacritics = str1
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
        const str2WithoutDiacritics = str2
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
        return str1WithoutDiacritics.includes(str2WithoutDiacritics)
    }
    const indexOfWithoutDiacritics = (str1: string, str2: string) => {
        const str1WithoutDiacritics = str1
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
        const str2WithoutDiacritics = str2
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
        return str1WithoutDiacritics.indexOf(str2WithoutDiacritics)
    }

    return {
        lastInputFound,
        results,
        fetching,
        getSearchResultsTemata,
        getSearchResultsKeStazeni,
        executeSearch,
        searchInPages,
        searchInBooks,
        searchKeywords,
        resetSearch,
        includesWithoutDiacritics,
        indexOfWithoutDiacritics,
    }
})
/* const useSearchStore = defineStore("search", {
    state: (): SearchStore => {
        return {
            lastInputFound: "",
            results: {
                KLICOVA_SLOVA: [],
                TEMATA: [],
                METRIKY: [],
                KLOK: [],
                STRANKY: [],
                LIMITY: [],
                JEVY: [],
                INDIKATORY: [],
                ATLAS: [],
            },
            fetching: false,
        }
    },
    getters: {
        getSearchResultsTemata(state) {
            // téma 0000 je skrytý odkaz na stiahnutie knihy Kraj
            return state.results.TEMATA.filter(
                el => el.ID_METADATA && !el.DOKUMENT
            )
        },
        getSearchResultsKeStazeni(state) {
            const { t } = useI18n()
            const keStazeni = state.results.TEMATA.filter(
                el => el.DOKUMENT
            ).map(el => {
                return {
                    ...el,
                    HTML: t("search.kestazeni_disclaimer"),
                }
            })
            return keStazeni
        },
    },
    actions: {
        async executeSearch(input: string, lang: LanguageCode = "cs") {
            const mainStore = useMainStore()
            mainStore.mobileMenuOpen = false
            this.fetching = true
            const localePath = useLocalePath()
            let searchUrl = SEARCH_URL + "?text=" + input
            if (lang === "en") {
                searchUrl += "&lang=en"
            }
            try {
                const data = await $fetch<SearchResults>(searchUrl, {
                    baseURL: import.meta.client
                        ? mainStore.apiBaseUrl
                        : mainStore.apiBaseUrlInt,
                })
                this.lastInputFound = input
                const stranky = [
                    ...this.searchInBooks(input, lang),
                    ...this.searchInPages(input),
                ]
                this.results = {
                    ...data,
                    STRANKY: stranky,
                }
                console.log(this.results)
                await navigateTo(
                    localePath({ name: "vyhledavani", query: { text: input } })
                )
            } catch (error) {
                console.log(error)
            } finally {
                this.fetching = false
            }
        },
        searchInPages(input: string) {
            const mainStore = useMainStore()
            const foundPageResults: PageItem[] = []
            const foundPageResultIds: string[] = []
            const keywords = this.searchKeywords(input)
            console.log(keywords)
            mainStore.getAllPages.forEach(page => {
                // search page keywords
                const hasFoundKws = keywords.some(kw => {
                    if ("keywords" in page) {
                        return page.keywords
                            ?.split(",")
                            .includes(kw.ID.toString())
                    }
                })
                if (hasFoundKws) {
                    foundPageResults.push(page)
                    foundPageResultIds.push(page.to)
                }
                // search page name
                if (
                    this.includesWithoutDiacritics(
                        useLocaleValue(page, "title"),
                        input
                    ) &&
                    !foundPageResultIds.includes(page.to)
                ) {
                    foundPageResults.push(page)
                    foundPageResultIds.push(page.to)
                }
                // seach page description
                if (
                    this.includesWithoutDiacritics(
                        useLocaleValue(page, "description"),
                        input
                    ) &&
                    !foundPageResultIds.includes(page.to)
                ) {
                    foundPageResults.push(page)
                    foundPageResultIds.push(page.to)
                }
            })
            return foundPageResults
        },
        searchInBooks(input: string, lang: LanguageCode = "cs") {
            const bookStore = useBooksStore()
            const foundBookResults: BookItem[] = []
            bookStore.books[lang].forEach(book => {
                if (
                    this.includesWithoutDiacritics(book.title, input) ||
                    this.includesWithoutDiacritics(book.subtitle, input) ||
                    book.kod.includes(input)
                ) {
                    foundBookResults.push(book)
                }
            })
            return foundBookResults
        },
        searchKeywords(input: string) {
            const mainStore = useMainStore()
            const foundKws = mainStore.keywords.filter(keyword =>
                useLocaleValue(keyword, "NAZEV").includes(input)
            )
            return foundKws
        },
        resetSearch() {
            this.results = {
                KLICOVA_SLOVA: [],
                TEMATA: [],
                METRIKY: [],
                KLOK: [],
                STRANKY: [],
                LIMITY: [],
                JEVY: [],
                INDIKATORY: [],
                ATLAS: [],
            }
            this.lastInputFound = ""
        },
        includesWithoutDiacritics(str1: string, str2: string) {
            const str1WithoutDiacritics = str1
                .toLowerCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
            const str2WithoutDiacritics = str2
                .toLowerCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
            return str1WithoutDiacritics.includes(str2WithoutDiacritics)
        },
        indexOfWithoutDiacritics(str1: string, str2: string) {
            const str1WithoutDiacritics = str1
                .toLowerCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
            const str2WithoutDiacritics = str2
                .toLowerCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
            return str1WithoutDiacritics.indexOf(str2WithoutDiacritics)
        },
    },
}) */

if ((import.meta as MetaHot).hot) {
    ;(import.meta as MetaHot).hot?.accept(
        acceptHMRUpdate(useSearchStore, (import.meta as MetaHot).hot)
    )
}
